import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { DisputeReasonType, ResolutionReasonType } from '@sharecost/shared';
import {
  fetchExpenseDisputes,
  fetchGroupDisputeCounts,
  createDispute,
  withdrawDispute,
  rejectDispute,
  resolveDispute,
  reopenDispute,
} from '@/api/disputes';
import { Analytics } from '@/services/analytics.service';

export function useExpenseDisputes(expenseId: string | undefined) {
  return useQuery({
    queryKey: ['disputes', expenseId],
    queryFn: () => fetchExpenseDisputes(expenseId!),
    enabled: !!expenseId,
  });
}

export function useGroupDisputeCounts(groupId: string | undefined) {
  return useQuery({
    queryKey: ['dispute-counts', groupId],
    queryFn: () => fetchGroupDisputeCounts(groupId!),
    enabled: !!groupId,
  });
}

function invalidateDisputeQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  expenseId: string,
  groupId?: string,
) {
  queryClient.invalidateQueries({ queryKey: ['disputes', expenseId] });
  queryClient.invalidateQueries({ queryKey: ['expenses'] });
  queryClient.invalidateQueries({ queryKey: ['activities'] });
  if (groupId) {
    queryClient.invalidateQueries({ queryKey: ['dispute-counts', groupId] });
  }
}

export function useCreateDispute() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      expenseId: string;
      groupId: string;
      userId: string;
      reasonType: DisputeReasonType;
      reasonText?: string;
    }) => createDispute(data.expenseId, data.groupId, data.userId, data.reasonType, data.reasonText),
    onSuccess: (_, { expenseId, groupId, reasonType }) => {
      invalidateDisputeQueries(queryClient, expenseId, groupId);
      Analytics.track({
        name: 'Dispute Raised',
        properties: { expense_id: expenseId, group_id: groupId, reason: reasonType },
      });
    },
  });
}

export function useWithdrawDispute() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { disputeId: string; expenseId: string; groupId?: string }) =>
      withdrawDispute(data.disputeId),
    onSuccess: (_, { expenseId, groupId }) => {
      invalidateDisputeQueries(queryClient, expenseId, groupId);
    },
  });
}

export function useRejectDispute() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      disputeId: string;
      expenseId: string;
      groupId?: string;
      userId: string;
      reasonType?: ResolutionReasonType;
      note?: string;
    }) => rejectDispute(data.disputeId, data.userId, data.reasonType, data.note),
    onSuccess: (_, { expenseId, groupId }) => {
      invalidateDisputeQueries(queryClient, expenseId, groupId);
    },
  });
}

export function useResolveDispute() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      disputeId: string;
      expenseId: string;
      groupId?: string;
      userId: string;
      note?: string;
    }) => resolveDispute(data.disputeId, data.userId, data.note),
    onSuccess: (_, { expenseId, groupId }) => {
      invalidateDisputeQueries(queryClient, expenseId, groupId);
      Analytics.track({
        name: 'Dispute Resolved',
        properties: { expense_id: expenseId, group_id: groupId ?? '', resolution: 'expense_edited' },
      });
    },
  });
}

export function useReopenDispute() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { disputeId: string; expenseId: string; groupId?: string }) =>
      reopenDispute(data.disputeId),
    onSuccess: (_, { expenseId, groupId }) => {
      invalidateDisputeQueries(queryClient, expenseId, groupId);
    },
  });
}
